import { useEffect, useState } from 'react';
import { api } from '../../api';
import { data, nomeDoContato, plural } from '../../formato';
import { useSessao } from '../../sessao';
import { Area, Campo, CampoNumerico, Carregando, Dialogo, ErroDaApi, Escolha, FotoProtegida, LinkProtegido } from '../../ui';
import { tamanho } from './Conversa';
import { coordenada, type ArquivoDaMensagem } from './MidiasDaMensagem';

/**
 * As janelas da conversa — onda 550 (Plano 2 · Comercial, Etapa 1, Tarefa 14).
 *
 * As ações que pedem alguma coisa à pessoa abrem no `Dialogo` da casa
 * (P-JAN-01): Transferir (para quem, e a nota), Registrar ligação (a
 * direção, a duração e o resumo), Devolver ao agente (o que acontece) e
 * Encerrar (o motivo, se a pessoa quiser deixá-lo). «Assumir» não abre
 * janela: não pede nada, e confirmar o que não é crítico só ensina a mão a
 * clicar duas vezes (P-UI-28).
 *
 * O rodapé é o da casa (P-JAN-04): o secundário à esquerda, a ação à
 * direita — e é sempre o ÚLTIMO filho direto do `Dialogo` (fora do `div` do
 * formulário), que é o que a base reconhece para prendê-lo embaixo, na
 * faixa recuada, com só o corpo rolando (P-JAN-03). O «Gravar» que ainda
 * não pode gravar fica à vista, indisponível (`aria-disabled`), com a
 * frase do que falta logo acima (P-UI-25) — a
 * ligação sem duração diz «Para gravar, falta: a duração da ligação.».
 * Todo motivo escrito tem 10 caracteres ou mais (P-TXT-10).
 */

/** A frase do que falta, com «e» antes do último («a duração e o resumo»), e o verbo da ação. */
export function faltaPara(acao: string, itens: string[]): string | null {
  if (!itens.length) return null;
  const lista = itens.length === 1 ? itens[0]
    : `${itens.slice(0, -1).join(', ')} e ${itens[itens.length - 1]}`;
  return `Para ${acao}, falta: ${lista}.`;
}

/** O rodapé de ação da casa: Cancelar à esquerda, a ação à direita. */
export function Rodape({ acao, falta, ocupado, aoCancelar, aoAgir, perigo = false }: {
  acao: string; falta: string | null; ocupado: boolean; aoCancelar(): void; aoAgir(): void; perigo?: boolean;
}) {
  return (
    <>
      {falta && <p className="o-que-falta janela-falta">{falta}</p>}
      <div className="rodape-dialogo">
        <button type="button" className="botao secundario" onClick={aoCancelar}>Cancelar</button>
        <button type="button" className={perigo ? 'botao perigo' : 'botao'}
          aria-disabled={falta !== null || ocupado}
          onClick={() => { if (falta === null && !ocupado) aoAgir(); }}>
          {ocupado ? `${acao}…` : acao}
        </button>
      </div>
    </>
  );
}

// ── Registrar ligação ────────────────────────────────────────────────

/** O que «Chamar no WhatsApp» leva à Nova conversa (onda 554, Tarefa 8; spec §3.4). */
export interface ChamarNoWhatsapp {
  contatoId?: string;
  numero?: string;
  oportunidadeId?: string;
  origem: 'telefone';
}

/** O contato da conversa, para a ligação saber quem chamar no WhatsApp. */
export interface ContatoDaLigacao { id: string; whatsapp: string | null; telefone: string | null }

/**
 * Para quem «Chamar no WhatsApp» abre a Nova conversa: o contato, quando ele
 * tem WhatsApp; o número, quando o telefone dele é de celular (a Nova
 * conversa o acha pelo telefone); senão o contato, e a janela pede o número.
 */
export function chamarOContato(ct: ContatoDaLigacao, oportunidadeId?: string | null): ChamarNoWhatsapp {
  const celular = !ct.whatsapp && ct.telefone ? celularDoTelefone(ct.telefone) : null;
  return {
    ...(celular ? { numero: celular } : { contatoId: ct.id }),
    ...(oportunidadeId ? { oportunidadeId } : {}),
    origem: 'telefone',
  };
}

/**
 * O telefone de celular do Brasil, normalizado («5547991045521»): 9 dígitos
 * depois do DDD, começando por 9 (spec §3.5). O fixo, o 0800 e o de fora
 * não são celular.
 */
export function celularDoTelefone(t: string | null | undefined): string | null {
  if (!t || !/^[+()\d .-]+$/.test(t.trim())) return null;
  const d = t.replace(/\D/g, '');
  const n = /^[1-9][1-9]9\d{8}$/.test(d) ? `55${d}` : d;
  return /^55[1-9][1-9]9\d{8}$/.test(n) ? n : null;
}

/** Os dígitos como a API normaliza no Brasil: o DDD e o número (10 ou 11) ganham o 55. */
const numeroBR = (d: string) => (/^[1-9][1-9]\d{8,9}$/.test(d) ? `55${d}` : d);

interface AchadoDaLigacao { id: string; nome: string; whatsapp: string | null; telefone: string | null; oportunidadeAbertaId: string | null }

/**
 * A ligação: na conversa aberta (`conversaId`) ou de um número (sem conversa:
 * a API acha o contato pelo telefone, ou cria, e abre a conversa no canal
 * Telefone). A duração é em minutos e segundos — ninguém lembra de uma
 * ligação em segundos — e vai à API em segundos.
 *
 * Onda 554 (Tarefa 8; spec §3.4, quadro 4): duas caixas embaixo.
 *  · «Abrir oportunidade» — só para quem edita o Comercial (`crm.editar`);
 *    marcada quando o contato é novo ou não tem oportunidade aberta (B3),
 *    desmarcada quando já tem (quem registra pode trocar). A conversa de
 *    telefone que já tem uma em andamento fica com ela.
 *  · «Chamar no WhatsApp depois de gravar» — desmarcada; marcada, gravar
 *    devolve `chamar` a quem abriu a janela, que abre a Nova conversa com o
 *    número e a oportunidade da ligação.
 */
export function JanelaDaLigacao({ aberta, conversaId, contato, contatoDaConversa, aoFechar, aoGravar }: {
  aberta: boolean;
  conversaId?: string;
  contato?: string;
  /** O contato da conversa aberta: as oportunidades dele (o padrão da caixa) e o «Chamar no WhatsApp». */
  contatoDaConversa?: ContatoDaLigacao;
  aoFechar(): void;
  aoGravar(r: { conversaId?: string; oportunidadeId?: string | null; chamar?: ChamarNoWhatsapp }): void;
}) {
  const { pode } = useSessao();
  const editaOComercial = pode('crm.editar');
  const [direcao, setDirecao] = useState<'recebida' | 'feita'>('recebida');
  const [minutos, setMinutos] = useState('');
  const [segundos, setSegundos] = useState('');
  const [resumo, setResumo] = useState('');
  const [telefone, setTelefone] = useState('');
  const [nome, setNome] = useState('');
  const [abrirOportunidade, setAbrirOportunidade] = useState(true);
  /* Quem tocou na caixa decide; antes disso, o padrão segue o contato (B3). */
  const [tocou, setTocou] = useState(false);
  const [chamar, setChamar] = useState(false);
  /* O dono do número digitado: `undefined` enquanto não se sabe, `null` quando é número novo. */
  const [dono, setDono] = useState<AchadoDaLigacao | null | undefined>(undefined);
  const [abertasDaConversa, setAbertasDaConversa] = useState<{ id: string; ligadaA: string[] }[] | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const [ocupado, setOcupado] = useState(false);

  useEffect(() => {
    if (!aberta) return;
    setDirecao('recebida'); setMinutos(''); setSegundos(''); setResumo('');
    setTelefone(''); setNome(''); setErro(null);
    setAbrirOportunidade(true); setTocou(false); setChamar(false); setDono(undefined); setAbertasDaConversa(null);
  }, [aberta]);

  /* Na conversa: as oportunidades abertas do contato dizem o padrão da caixa. */
  const contatoId = contatoDaConversa?.id ?? null;
  useEffect(() => {
    if (!aberta || !conversaId || !contatoId || !editaOComercial) return undefined;
    let vale = true;
    api<{ id: string; ligadaA: string[] }[]>(`/comercial/contatos/${contatoId}/oportunidades`)
      .then((l) => { if (vale) setAbertasDaConversa(l); })
      .catch(() => { if (vale) setAbertasDaConversa([]); });
    return () => { vale = false; };
  }, [aberta, conversaId, contatoId, editaOComercial]);

  /* De um número: de quem ele é (a busca da Nova conversa acha pelo WhatsApp e pelo telefone). */
  const digitos = telefone.replace(/\D/g, '');
  useEffect(() => {
    if (!aberta || conversaId) return undefined;
    if (digitos.length < 10) { setDono(undefined); return undefined; }
    let vale = true;
    const t = window.setTimeout(() => {
      api<AchadoDaLigacao[]>(`/comercial/contatos/busca?q=${encodeURIComponent(digitos)}`)
        .then((l) => {
          if (!vale) return;
          const n = numeroBR(digitos);
          setDono(l.find((c) => c.whatsapp === n || numeroBR((c.telefone ?? '').replace(/\D/g, '')) === n) ?? null);
        })
        .catch(() => { if (vale) setDono(null); });
    }, 250);
    return () => { vale = false; window.clearTimeout(t); };
  }, [aberta, conversaId, digitos]);

  /* O padrão (B3): marcada para contato novo ou sem oportunidade aberta. */
  const temAberta = conversaId ? (abertasDaConversa ? abertasDaConversa.length > 0 : null)
    : dono === undefined ? null : !!dono?.oportunidadeAbertaId;
  useEffect(() => {
    if (!tocou && temAberta !== null) setAbrirOportunidade(!temAberta);
  }, [temAberta, tocou]);

  const inteiro = (v: string) => (/^\d+$/.test(v.trim()) ? Number(v.trim()) : null);
  const min = minutos.trim() === '' ? null : inteiro(minutos);
  const seg = segundos.trim() === '' ? 0 : inteiro(segundos);
  const temDuracao = min !== null && seg !== null && seg < 60 && (min > 0 || seg > 0);
  const duracaoS = temDuracao ? min! * 60 + seg! : null;

  const falta = faltaPara('gravar', [
    ...(!conversaId && digitos.length < 10 ? ['o telefone com o DDD'] : []),
    ...(!temDuracao ? ['a duração da ligação'] : []),
    ...(resumo.trim().length < 10 ? ['o resumo (10 caracteres ou mais)'] : []),
  ]);

  const gravar = async () => {
    setOcupado(true);
    setErro(null);
    try {
      const abre = editaOComercial && abrirOportunidade;
      const corpo = { direcao, duracaoSegundos: duracaoS, resumo: resumo.trim(), ...(abre ? { abrirOportunidade: true } : {}) };
      if (conversaId) {
        const r = await api<{ oportunidadeId: string | null }>(`/comercial/conversas/${conversaId}/ligacoes`, { metodo: 'POST', corpo });
        /* A oportunidade da conversa de telefone: a que a ligação abriu, ou a aberta ligada a ela. */
        const daConversa = abertasDaConversa?.find((o) => o.ligadaA.includes(conversaId))?.id ?? null;
        const op = editaOComercial ? (r?.oportunidadeId ?? daConversa) : null;
        aoGravar({
          conversaId, oportunidadeId: r?.oportunidadeId ?? null,
          ...(chamar && contatoDaConversa ? { chamar: chamarOContato(contatoDaConversa, op) } : {}),
        });
      } else {
        const r = await api<{ conversaId: string; contatoId: string; oportunidadeId: string | null }>('/comercial/ligacoes', {
          metodo: 'POST',
          corpo: { ...corpo, telefone: telefone.trim(), ...(nome.trim() ? { nome: nome.trim() } : {}) },
        });
        const op = editaOComercial ? r.oportunidadeId ?? dono?.oportunidadeAbertaId ?? null : null;
        /* O dono conhecido é chamado pelo WhatsApp dele (que pode não ser o número que ligou, o fixo da
           empresa); sem dono, o número só se for celular; senão a Nova conversa pede o WhatsApp. */
        const celular = celularDoTelefone(telefone);
        const alvo: ChamarNoWhatsapp = dono ? chamarOContato(dono, op)
          : celular ? { numero: celular, ...(op ? { oportunidadeId: op } : {}), origem: 'telefone' }
            : { contatoId: r.contatoId, ...(op ? { oportunidadeId: op } : {}), origem: 'telefone' };
        aoGravar({
          conversaId: r.conversaId, oportunidadeId: r.oportunidadeId ?? null, ...(chamar ? { chamar: alvo } : {}),
        });
      }
    } catch (e) {
      setErro(e);
    } finally {
      setOcupado(false);
    }
  };

  const dicaDoTelefone = dono === null ? 'Número novo: o contato será criado.'
    : dono ? `É o número de ${nomeDoContato(dono.nome)}.` : 'Do contato que ligou ou para quem você ligou.';
  return (
    <Dialogo aberto={aberta} titulo="Registrar ligação" rotulo={contato} aoFechar={aoFechar} classe="janela-da-ligacao">
      <div className="janela-ligacao">
        {!conversaId && (
          <div className="janela-linha">
            <Campo rotulo="Telefone" type="tel" inputMode="tel" autoComplete="off" value={telefone} name="telefone"
              placeholder="(47) 99999-1234" onChange={(e) => setTelefone(e.target.value)} dica={dicaDoTelefone} />
            <Campo rotulo="Nome" type="text" value={nome} name="nome" onChange={(e) => setNome(e.target.value)}
              dica={dono ? 'O contato já existe: o nome fica o dele.' : 'Só quando o número ainda não é de um contato.'} />
          </div>
        )}
        <Escolha rotulo="A ligação" limpavel={false} valor={direcao}
          aoEscolher={(v) => setDirecao((v as 'recebida' | 'feita') ?? 'recebida')}
          opcoes={[{ valor: 'recebida', texto: 'O cliente ligou' }, { valor: 'feita', texto: 'Nós ligamos' }]} />
        <div className="janela-linha janela-duracao">
          <CampoNumerico rotulo="Duração (minutos)" inteiro name="minutos" value={minutos}
            onChange={(e) => setMinutos(e.target.value)} />
          <CampoNumerico rotulo="e segundos" inteiro name="segundos" value={segundos}
            onChange={(e) => setSegundos(e.target.value)} />
        </div>
        <Area rotulo="Resumo" rows={4} value={resumo} onChange={(e) => setResumo(e.target.value)}
          dica="O que se tratou e o que ficou combinado. Fica na conversa, para quem vier depois." />
        {editaOComercial && (
          <label className="caixa-marcar ligacao-marca ligacao-oportunidade">
            <input type="checkbox" checked={abrirOportunidade}
              onChange={(e) => { setTocou(true); setAbrirOportunidade(e.target.checked); }} />
            <span>
              <strong>Abrir oportunidade</strong>
              <span className="sub">
                {temAberta && !abrirOportunidade
                  ? 'Ele já tem oportunidade aberta. Marque se este for outro pedido.'
                  : 'Em Novo, com você como responsável. Já vem marcado para contato novo ou sem oportunidade aberta; desmarque se for fornecedor, engano ou outro assunto.'}
              </span>
            </span>
          </label>
        )}
        <label className="caixa-marcar ligacao-marca ligacao-chamar">
          <input type="checkbox" checked={chamar} onChange={(e) => setChamar(e.target.checked)} />
          <span>
            <strong>Chamar no WhatsApp depois de gravar</strong>
            <span className="sub">Abre a Nova conversa com este número.</span>
          </span>
        </label>
        <ErroDaApi erro={erro} />
      </div>
      <Rodape acao="Gravar" falta={falta} ocupado={ocupado} aoCancelar={aoFechar} aoAgir={() => void gravar()} />
    </Dialogo>
  );
}

// ── Transferir ───────────────────────────────────────────────────────

interface Destinos {
  pessoas: { id: string; nome: string }[];
  rotas: { codigo: string; nome: string; responsavel: string | null }[];
}

/**
 * Para uma pessoa (quem pode receber a conversa: responde e a vê) ou para
 * uma rota (vai ao responsável dela, com o prazo do substituto de novo). A
 * nota vira nota interna na conversa — o que quem recebe precisa saber.
 */
export function JanelaDeTransferir({ aberta, conversaId, contato, aoFechar, aoTransferir }: {
  aberta: boolean; conversaId: string; contato: string; aoFechar(): void; aoTransferir(nome: string): void;
}) {
  const [destinos, setDestinos] = useState<Destinos | null>(null);
  const [para, setPara] = useState<'pessoa' | 'rota'>('pessoa');
  const [pessoa, setPessoa] = useState('');
  const [rota, setRota] = useState('');
  const [nota, setNota] = useState('');
  const [prioridade, setPrioridade] = useState(false);
  const [erro, setErro] = useState<unknown>(null);
  const [ocupado, setOcupado] = useState(false);

  useEffect(() => {
    if (!aberta) return;
    setPara('pessoa'); setPessoa(''); setRota(''); setNota(''); setPrioridade(false); setErro(null);
    setDestinos(null);
    api<Destinos>(`/comercial/conversas/${conversaId}/destinos`).then(setDestinos).catch(setErro);
  }, [aberta, conversaId]);

  const falta = faltaPara('transferir', [
    ...(para === 'pessoa' && !pessoa ? ['a pessoa que recebe'] : []),
    ...(para === 'rota' && !rota ? ['a rota'] : []),
  ]);

  const transferir = async () => {
    setOcupado(true);
    setErro(null);
    try {
      await api(`/comercial/conversas/${conversaId}/transferir`, {
        metodo: 'POST',
        corpo: {
          ...(para === 'pessoa' ? { usuarioId: pessoa } : { rota }),
          ...(nota.trim() ? { nota: nota.trim() } : {}),
          ...(prioridade ? { prioridade: true } : {}),
        },
      });
      const nome = para === 'pessoa'
        ? destinos?.pessoas.find((x) => x.id === pessoa)?.nome ?? ''
        : destinos?.rotas.find((x) => x.codigo === rota)?.nome ?? '';
      aoTransferir(nome);
    } catch (e) {
      setErro(e);
    } finally {
      setOcupado(false);
    }
  };

  return (
    <Dialogo aberto={aberta} titulo="Transferir a conversa" rotulo={contato} aoFechar={aoFechar}>
      <div className="janela-transferir">
        <Escolha rotulo="Para" limpavel={false} valor={para}
          aoEscolher={(v) => setPara((v as 'pessoa' | 'rota') ?? 'pessoa')}
          opcoes={[{ valor: 'pessoa', texto: 'Uma pessoa' }, { valor: 'rota', texto: 'Uma rota' }]} />
        {para === 'pessoa' ? (
          <label className="campo">
            <span className="rotulo">Pessoa</span>
            <select value={pessoa} onChange={(e) => setPessoa(e.target.value)} disabled={!destinos}>
              <option value="">{destinos ? 'Escolha quem recebe' : 'Carregando a equipe'}</option>
              {destinos?.pessoas.map((x) => <option key={x.id} value={x.id}>{x.nome}</option>)}
            </select>
            {destinos && destinos.pessoas.length === 0 && (
              <span className="dica">Ninguém mais da equipe pode receber esta conversa. Transfira para uma rota.</span>
            )}
          </label>
        ) : (
          <label className="campo">
            <span className="rotulo">Rota</span>
            <select value={rota} onChange={(e) => setRota(e.target.value)} disabled={!destinos}>
              <option value="">{destinos ? 'Escolha a rota' : 'Carregando as rotas'}</option>
              {destinos?.rotas.map((x) => (
                <option key={x.codigo} value={x.codigo}>
                  {x.responsavel ? `${x.nome} — ${x.responsavel}` : `${x.nome} — sem responsável`}
                </option>
              ))}
            </select>
            <span className="dica">A conversa vai ao responsável da rota, com o prazo do substituto de novo.</span>
          </label>
        )}
        <Area rotulo="Nota para quem recebe" rows={3} value={nota} onChange={(e) => setNota(e.target.value)}
          dica="Opcional. Fica na conversa como nota interna; o cliente não a vê." />
        <label className="caixa-marcar">
          <input type="checkbox" checked={prioridade} onChange={(e) => setPrioridade(e.target.checked)} />
          Marcar como prioridade
        </label>
        <ErroDaApi erro={erro} />
      </div>
      <Rodape acao="Transferir" falta={falta} ocupado={ocupado} aoCancelar={aoFechar}
        aoAgir={() => void transferir()} />
    </Dialogo>
  );
}

// ── Devolver ao agente ───────────────────────────────────────────────

export function JanelaDeDevolver({ aberta, conversaId, contato, aoFechar, aoDevolver }: {
  aberta: boolean; conversaId: string; contato: string; aoFechar(): void; aoDevolver(): void;
}) {
  const [erro, setErro] = useState<unknown>(null);
  const [ocupado, setOcupado] = useState(false);
  useEffect(() => { if (aberta) setErro(null); }, [aberta]);
  const devolver = async () => {
    setOcupado(true);
    setErro(null);
    try {
      await api(`/comercial/conversas/${conversaId}/devolver-ao-agente`, { metodo: 'POST' });
      aoDevolver();
    } catch (e) { setErro(e); } finally { setOcupado(false); }
  };
  return (
    <Dialogo aberto={aberta} titulo="Devolver ao agente" rotulo={contato} aoFechar={aoFechar}>
      <p>
        O agente volta a acompanhar a conversa e a sugerir as respostas. Ela sai da sua lista e o prazo do
        substituto para de contar; quando o agente precisar de uma pessoa, ele a passa de novo.
      </p>
      <ErroDaApi erro={erro} />
      <Rodape acao="Devolver" falta={null} ocupado={ocupado} aoCancelar={aoFechar} aoAgir={() => void devolver()} />
    </Dialogo>
  );
}

// ── Encerrar ─────────────────────────────────────────────────────────

export function JanelaDeEncerrar({ aberta, conversaId, contato, aoFechar, aoEncerrar }: {
  aberta: boolean; conversaId: string; contato: string; aoFechar(): void; aoEncerrar(): void;
}) {
  const [motivo, setMotivo] = useState('');
  const [erro, setErro] = useState<unknown>(null);
  const [ocupado, setOcupado] = useState(false);
  useEffect(() => { if (aberta) { setMotivo(''); setErro(null); } }, [aberta]);
  const curto = motivo.trim().length > 0 && motivo.trim().length < 10;
  const falta = curto ? `O motivo, quando escrito, tem 10 caracteres ou mais — faltam ${
    plural(10 - motivo.trim().length, 'caractere', 'caracteres')}.` : null;
  const encerrar = async () => {
    setOcupado(true);
    setErro(null);
    try {
      await api(`/comercial/conversas/${conversaId}/encerrar`, {
        metodo: 'POST', corpo: motivo.trim() ? { motivo: motivo.trim() } : {},
      });
      aoEncerrar();
    } catch (e) { setErro(e); } finally { setOcupado(false); }
  };
  return (
    <Dialogo aberto={aberta} titulo="Encerrar a conversa" rotulo={contato} aoFechar={aoFechar}>
      <p>
        A conversa sai da caixa de entrada. Se o cliente escrever de novo, uma conversa nova aparece, com o
        histórico desta.
      </p>
      <Area rotulo="Motivo" rows={3} value={motivo} onChange={(e) => setMotivo(e.target.value)}
        dica="Opcional. Fica na conversa como nota interna." />
      <ErroDaApi erro={erro} />
      <Rodape acao="Encerrar" falta={falta} ocupado={ocupado} aoCancelar={aoFechar} aoAgir={() => void encerrar()} />
    </Dialogo>
  );
}

// ── Trocar o local da obra? (onda 555, §3.1.1) ──────────────────────

/** O local como a API o devolve no 409 do «Usar como local da obra». */
export interface LocalDaObra { latitude: number; longitude: number; endereco: string | null }

const localLegivel = (l: LocalDaObra) => l.endereco?.trim() || coordenada(l.latitude, l.longitude);

/**
 * A oportunidade já tem um local, e a localização traz outro: a janela
 * mostra o atual e o novo; «Trocar o local» grava o novo (`trocar: true`).
 */
export function JanelaDoLocalDaObra({ aberta, contato, atual, novo, ocupado, erro, aoFechar, aoTrocar }: {
  aberta: boolean; contato: string; atual: LocalDaObra | null; novo: LocalDaObra | null;
  ocupado: boolean; erro: unknown; aoFechar(): void; aoTrocar(): void;
}) {
  return (
    <Dialogo aberto={aberta} titulo="Trocar o local da obra?" rotulo={contato} aoFechar={aoFechar} classe="janela-local-da-obra">
      <p>A oportunidade já tem um local. Troque pelo da localização que o cliente mandou, ou deixe o atual.</p>
      <dl className="janela-local-pares">
        <div className="janela-local-par">
          <dt>Atual</dt>
          <dd>{atual ? localLegivel(atual) : ''}</dd>
        </div>
        <div className="janela-local-par novo">
          <dt>Novo</dt>
          <dd>{novo ? localLegivel(novo) : ''}</dd>
        </div>
      </dl>
      <ErroDaApi erro={erro} />
      <Rodape acao="Trocar o local" falta={null} ocupado={ocupado} aoCancelar={aoFechar} aoAgir={aoTrocar} />
    </Dialogo>
  );
}

// ── A galeria das mídias do contato (onda 555, §3.1) ────────────────

/** Uma mídia do contato (`GET /comercial/contatos/:id/midias`). */
export interface MidiaDoContato {
  mensagemId: string; conversaId: string; tipo: 'imagem' | 'video' | 'arquivo';
  arquivo: ArquivoDaMensagem; criadaEm: string;
}

/** A extensão do arquivo, em maiúsculas, para a miniatura do documento («PDF»). */
export const extensao = (nome: string) => (/\.([a-z0-9]{1,5})$/i.exec(nome)?.[1] ?? '').toUpperCase();

/** A miniatura de uma mídia: a imagem, ou o quadro do vídeo ou do documento. */
export function MiniaturaDaMidia({ m }: { m: MidiaDoContato }) {
  if (m.tipo === 'imagem' && m.arquivo.mime.startsWith('image/')) {
    return (
      <FotoProtegida arquivoId={m.arquivo.id} alt={m.arquivo.nome} className="midia-miniatura-imagem"
        carregando={<span className="midia-miniatura-rotulo">Imagem</span>}
        semImagem={<span className="midia-miniatura-rotulo">Imagem</span>} />
    );
  }
  return <span className="midia-miniatura-rotulo">{m.tipo === 'video' ? 'Vídeo' : extensao(m.arquivo.nome) || 'Arquivo'}</span>;
}

/**
 * As mídias do contato neste canal, as mais novas primeiro (a API manda as
 * 60 mais novas das conversas que a pessoa vê). Cada uma abre ou baixa pela
 * rota de arquivo de hoje. A janela só informa: «Fechar» (P-TXT-09).
 */
export function JanelaDaGaleria({ aberta, contatoId, canal, rotulo, aoFechar }: {
  aberta: boolean; contatoId: string;
  /** O código do canal da conversa: a galeria é deste canal. */
  canal: string;
  rotulo: string;
  aoFechar(): void;
}) {
  const [midias, setMidias] = useState<MidiaDoContato[] | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  useEffect(() => {
    if (!aberta) return undefined;
    let vale = true;
    setMidias(null); setErro(null);
    api<MidiaDoContato[]>(`/comercial/contatos/${contatoId}/midias?canal=${encodeURIComponent(canal)}`)
      .then((l) => { if (vale) setMidias(l); })
      .catch((e) => { if (vale) { setMidias([]); setErro(e); } });
    return () => { vale = false; };
  }, [aberta, contatoId, canal]);
  return (
    <Dialogo aberto={aberta} titulo="Mídias, links e documentos" rotulo={rotulo} aoFechar={aoFechar} larga
      classe="janela-da-galeria">
      <ErroDaApi erro={erro} />
      {!midias ? <Carregando texto="Carregando as mídias" />
        : midias.length === 0 ? (!erro && <p className="painel-vazio">Nenhuma mídia deste contato neste canal ainda.</p>)
          : (
            <>
              <p className="galeria-conta">
                {plural(midias.length, 'mídia', 'mídias')}{midias.length >= 60 ? ' (as mais novas)' : ''}
              </p>
              <ul className="galeria">
                {midias.map((m) => (
                  <li key={m.mensagemId} className={`galeria-item ${m.tipo}`} data-mensagem={m.mensagemId}>
                    <LinkProtegido caminho={`/arquivos/${m.arquivo.id}/conteudo`} nomeQueda={m.arquivo.nome}
                      className="galeria-abrir">
                      <span className="midia-miniatura"><MiniaturaDaMidia m={m} /></span>
                      <span className="galeria-nome">{m.arquivo.nome}</span>
                      <span className="painel-aux">{data(m.criadaEm)} · {tamanho(m.arquivo.tamanho)}</span>
                    </LinkProtegido>
                  </li>
                ))}
              </ul>
            </>
          )}
      <div className="rodape-dialogo">
        <button type="button" className="botao secundario" onClick={aoFechar}>Fechar</button>
      </div>
    </Dialogo>
  );
}
